import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { employmentService } from '../../../../services/employment';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('EmploymentInfoData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const EmploymentInfoDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  const [employmentData, setEmploymentData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmploymentData();
  }, [targetUserId]);

  const loadEmploymentData = async () => {
    try {
      setLoading(true);
      // Pass targetUserId to fetch another user's data (for successor view)
      const response = await employmentService.getAllEmployment(targetUserId);
      logger.debug('[Employment Data Debug] Loaded all employment entries:', response, targetUserId ? `(viewing ${targetUserId})` : '');

      // Handle response format - check if it's wrapped in a success/data object
      const data = response?.data || response;
      logger.debug('[Employment Data Debug] Extracted data array:', data);
      logger.debug('[Employment Data Debug] Data length:', data?.length || 0);

      if (data && Array.isArray(data) && data.length > 0) {
        // Set all employment entries
        logger.debug('[Employment Data Debug] Setting all employment data entries:', data);
        setEmploymentData(data);
      } else {
        logger.debug('[Employment Data Debug] No employment entries found or invalid data format');
        setEmploymentData([]);
      }
    } catch (error) {
      logger.error('Error loading employment data:', error);
      Alert.alert('Error', 'Failed to load employment information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatSalary = (salary: number | undefined) => {
    if (!salary) return '';
    return `$${salary.toLocaleString()}`;
  };

  const handleEditEmployment = (employment: any) => {
    const mappedData = {
      id: employment.id,
      companyName: employment.companyName,
      jobTitle: employment.jobTitle,
      workAddress: employment.workAddress,
      workPhone: employment.workPhone,
      workEmail: employment.workEmail,
      supervisorName: employment.supervisorName,
      supervisorPhone: employment.supervisorPhone,
      supervisorEmail: employment.supervisorEmail,
      hrContactName: employment.hrContactName,
      hrContactPhone: employment.hrContactPhone,
      hrContactEmail: employment.hrContactEmail,
      employeeId: employment.employeeId,
      salary: employment.salary,
      employmentType: employment.employmentType,
      benefitsInfo: employment.benefitsInfo,
      additionalNotes: employment.additionalNotes,
    };
    navigation.navigate('EmploymentInfoInput', {
      employmentData: mappedData,
      isEditing: true
    });
  };

  const handleDeleteEmployment = async (employment: any) => {
    Alert.alert('Delete', `Are you sure you want to delete employment at ${employment.companyName}?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await employmentService.deleteEmployment(employment.id);

            // Reload data after deletion
            await loadEmploymentData();

            // Check if there are any remaining employment entries
            const remainingData = await employmentService.getAllEmployment();
            const actualData = remainingData?.data || remainingData;
            if (!actualData || actualData.length === 0) {
              await tasksService.resetTask('employment_info');
              navigation.navigate('PersonalChecklist');
            }

            Alert.alert('Deleted', 'Employment information deleted.');
          } catch (e) {
            logger.error('Delete employment failed:', e);
            Alert.alert('Error', 'Failed to delete employment information');
          }
        }
      },
    ]);
  };

  const handleAddEmployment = () => {
    navigation.navigate('EmploymentInfoInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Employment Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading employment information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!employmentData || employmentData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Employment Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly ? 'This user has not added employment information yet' : 'No employment information found'}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddEmployment}
              >
                <Text style={styles.addButtonText}>Add Employment Info</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.addButton, { marginTop: 10, backgroundColor: '#6B7280' }]}
              onPress={() => navigation.navigate('PersonalChecklist')}
            >
              <Text style={styles.addButtonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Employment Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {employmentData.map((employment, index) => (
            <View key={employment.id || index} style={styles.employmentCard}>
              <View style={styles.cardHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.cardName}>
                    {employment.companyName || 'Unknown Company'}
                  </Text>
                  <Text style={styles.cardType}>
                    {employment.jobTitle || 'Job title not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editCardButton}
                      onPress={() => handleEditEmployment(employment)}
                    >
                      <Text style={styles.editCardButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteCardButton}
                      onPress={() => handleDeleteEmployment(employment)}
                    >
                      <Text style={styles.deleteCardButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              <InfoItem label="Company Name" value={employment.companyName || ''} />
              <InfoItem label="Job Title" value={employment.jobTitle || ''} />
              <InfoItem label="Work Address" value={employment.workAddress || ''} />
              <InfoItem label="Work Phone" value={formatPhoneNumber(employment.workPhone) || ''} />
              <InfoItem label="Work Email" value={employment.workEmail || ''} />
              <InfoItem label="Supervisor Name" value={employment.supervisorName || ''} />
              <InfoItem label="Supervisor Phone" value={formatPhoneNumber(employment.supervisorPhone) || ''} />
              <InfoItem label="Supervisor Email" value={employment.supervisorEmail || ''} />
              <InfoItem label="HR Contact Name" value={employment.hrContactName || ''} />
              <InfoItem label="HR Contact Phone" value={formatPhoneNumber(employment.hrContactPhone) || ''} />
              <InfoItem label="HR Contact Email" value={employment.hrContactEmail || ''} />
              <InfoItem label="Employee ID" value={employment.employeeId || ''} />
              {employment.salary && (
                <InfoItem label="Salary" value={formatSalary(employment.salary)} />
              )}
              <InfoItem label="Employment Type" value={employment.employmentType || ''} />
              <InfoItem label="Benefits Information" value={employment.benefitsInfo || ''} />
              <InfoItem label="Additional Notes" value={employment.additionalNotes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherEmploymentButton}
                onPress={handleAddEmployment}
              >
                <Text style={styles.addAnotherEmploymentButtonText}>Add Another Employment</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('PersonalChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  employmentCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  cardName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  cardType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editCardButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteCardButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherEmploymentButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherEmploymentButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});